/**
 * @name Topbar
 * @description Componente topbar de Talentry
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';

import { Lines } from '@components';
import { Flex, Icons, MUI, Theme, Typography } from '@styles';

import MenuAdmin from './components/menu-admin';
import MenuApps from './components/menu-apps';
import MenuNotifications from './components/menu-notifications';
import MenuUser from './components/menu-user';
import Sidebar from './components/sidebar';

// Material-UI 
import MuiAppBar from '@material-ui/core/AppBar';
import MuiToolbar from '@material-ui/core/Toolbar';

// Servicios de sesión
import { startSessionTimer, stopSessionTimer } from '@services/SessionTimer';
import { refreshToken, onAuthUpdated } from '@services/TokenManager';
import { AuthService, PreviousRouteService, SnackbarService } from '@services';

class Topbar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      openSidebar: false,
      isExternal: Boolean(JSON.parse(localStorage.getItem('ls.infoUsuario'))?.EsExterno) || false,
      // MODAL - Sesión
      showSessionExpiring: false,
      isRefreshing: false,
      remainLabel: '' // etiqueta amigable mm:ss
    };

    this._uiCountdownInterval = null;
    this._offAuthUpdated = null;
    this._onSessionExpiring = this._onSessionExpiring.bind(this);
    this._onSessionExpired = this._onSessionExpired.bind(this);
    this._onAuthUpdated = this._onAuthUpdated.bind(this);
    this._onStorage = this._onStorage.bind(this);
  }

  componentDidMount() {
    startSessionTimer(this.props.sessionWarnMinutes);
    window.addEventListener('session:expiring', this._onSessionExpiring);
    window.addEventListener('session:expired', this._onSessionExpired);

    // Escucha cross-tab (BroadcastChannel + window) vía helper centralizado
    this._offAuthUpdated = onAuthUpdated(this._onAuthUpdated);

    // Fallback robusto: si cambia ls.authorizationData en otra pestaña
    window.addEventListener('storage', this._onStorage);
  }

  componentWillUnmount() {
    window.removeEventListener('session:expiring', this._onSessionExpiring);
    window.removeEventListener('session:expired', this._onSessionExpired);
    window.removeEventListener('storage', this._onStorage);
    this._offAuthUpdated?.();
    stopSessionTimer();
    this._clearUiCountdown();
  }

  // -------------------------------------
  // Helpers keepSession / lock
  // -------------------------------------
  _hasKeepSession() {
    try {
      const creds = JSON.parse(localStorage.getItem('ls.credentials'));
      return !!(creds?.userName && creds?.password);
    } catch { return false; }
  }

  _takeAutoExtendLock(ttlMs = 15000) {
    try {
      const now = Date.now();
      const raw = localStorage.getItem('ls.autoExtendLock');
      const last = raw ? Number(raw) : 0;
      if (!last || (now - last) > ttlMs) {
        localStorage.setItem('ls.autoExtendLock', String(now));
        return true;
      }
      return false;
    } catch { return true; } // si storage falla, continuar (mejor extender que vencer)
  }

  _releaseAutoExtendLock() {
    // eslint-disable-next-line no-empty
    try { localStorage.removeItem('ls.autoExtendLock'); } catch { }
  }

  // -------------------------------------
  // Helpers cooldown auto-renovación
  // -------------------------------------
  _isInRenewalCooldown(cooldownMs = 2 * 60 * 1000) { // 2 minutos por defecto
    try {
      const lastRenewal = localStorage.getItem('ls.autoRenewalCooldown');
      if (!lastRenewal) return false;
      const now = Date.now();
      const timeSinceLastRenewal = now - Number(lastRenewal);
      return timeSinceLastRenewal < cooldownMs;
    } catch { return false; }
  }

  _setRenewalCooldown() {
    try {
      localStorage.setItem('ls.autoRenewalCooldown', String(Date.now()));
    } catch { /* ignore storage errors */ }
  }

  // -------------------------------------
  // Navegación / Sidebar
  // -------------------------------------
  goBack = (history) => {
    PreviousRouteService.getPreviousRoute()?.rootRoute
      ? history.push(PreviousRouteService.getPreviousRoute().rootRoute)
      : this.props.backRoute
        ? history.push(this.props.backRoute)
      : history.goBack();
  }

  sidebarToggleHandler = () => {
    const { openSidebar } = this.state;
    this.setState({ openSidebar: !openSidebar });
  }

  // -------------------------------------
  // Lógica de sesión / modal
  // -------------------------------------
  _onSessionExpiring() {
    //  Si hay keepSession (ls.credentials), auto-extiende sin mostrar modal
    if (this._hasKeepSession()) {
      // Verificar cooldown para evitar auto-renovaciones muy frecuentes
      if (this._isInRenewalCooldown()) {
        return;
      }

      // Evita que todas las pestañas refresquen al mismo tiempo
      if (this._takeAutoExtendLock()) {
        this._setRenewalCooldown();
        this.onContinueSession().finally(() => this._releaseAutoExtendLock());
      }
      return;
    }

    // Caso normal (sin keep): mostramos modal
    this.setState({ showSessionExpiring: true }, () => {
      this._startUiCountdown();
    });
  }

  _onSessionExpired() {
    this._clearUiCountdown();
    this.setState({ showSessionExpiring: false, isRefreshing: false, remainLabel: '' }, () => {
      PreviousRouteService.saveCurrentRoute();
      AuthService.clearStorage();
      AuthService.logout();
    });
  }

  _onAuthUpdated() {
    // Se disparará cuando cualquier pestaña ejecute setAuthData (BC + window)
    if (this.state.showSessionExpiring) {
      this._clearUiCountdown();
      this.setState({ showSessionExpiring: false, isRefreshing: false, remainLabel: '' });
    }
  }

  _onStorage(ev) {
    // Fallback: si otra pestaña modifica ls.authorizationData, cerramos modal
    if (ev.key === 'ls.authorizationData') {
      this._onAuthUpdated();
    }
  }

  _startUiCountdown() {
    this._clearUiCountdown();
    this._tickRemainLabel(); // Primera actualización inmediata
    this._uiCountdownInterval = setInterval(() => this._tickRemainLabel(), 1000);
  }

  _clearUiCountdown() {
    if (this._uiCountdownInterval) {
      clearInterval(this._uiCountdownInterval);
      this._uiCountdownInterval = null;
    }
  }

  _tickRemainLabel() {
    try {
      const auth = JSON.parse(localStorage.getItem('ls.authorizationData'));
      if (!auth?.expirationDate) {
        this.setState({ remainLabel: '' });
        return;
      }
      const exp = new Date(auth.expirationDate).getTime();
      const now = Date.now();
      const diffMs = Math.max(0, exp - now);
      const mm = Math.floor(diffMs / 60000);
      const ss = Math.floor((diffMs % 60000) / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      this.setState({ remainLabel: `${pad(mm)}:${pad(ss)}` });
    } catch {
      this.setState({ remainLabel: '' });
    }
  }

  onContinueSession = async () => {
    if (this.state.isRefreshing) return;
    this.setState({ isRefreshing: true });
    try {
      await refreshToken({
        allowLegacyLogin: true,
        credentialScope: this._hasKeepSession() ? 'keepSession' : 'manual'
      });
      this._clearUiCountdown();
      this.setState({ showSessionExpiring: false, isRefreshing: false, remainLabel: '' });
      if (!this._hasKeepSession()) {
        SnackbarService?.success?.(i18n.t('Login.sessionExtended'));
      }
    } catch {
      this._clearUiCountdown();
      this.setState({ showSessionExpiring: false, isRefreshing: false, remainLabel: '' });
    } finally {
      // Por si vino del flujo auto (con lock)
      this._releaseAutoExtendLock();
    }
  }

  onLogoutNow = () => {
    this._clearUiCountdown();
    this.setState({ showSessionExpiring: false, isRefreshing: false, remainLabel: '' }, () => {
      AuthService.clearStorage();
      AuthService.logout();
    });
  }

  onClose = () => {
    this._clearUiCountdown(); // Cierra y deja de actualizar el label
    this.setState({ showSessionExpiring: false });
  }

  // -------------------------------------

  render() {
    const {
      openSidebar,
      isExternal,
      showSessionExpiring,
      isRefreshing,
      remainLabel
    } = this.state;

    return (
      <>
        {/* Topbar */}
        <Styled.AppBar>
          <Styled.Toolbar>
            {!isExternal && (
              <>
                {/* Sidebar (Menú Lateral) */}
                {(!this.props.showBackButton && this.props.showSidebar) && (
                  <Styled.BoxTopbarIcon onClick={() => this.sidebarToggleHandler()}>
                    <MUI.Tooltip title={i18n.t('GenericMessages.sidebar')}>
                      {!openSidebar ? <Icons.Menu /> : <Icons.Close />}
                    </MUI.Tooltip>
                  </Styled.BoxTopbarIcon>
                )}

                <Sidebar
                  open={openSidebar}
                  appName={this.props.appName}
                  appIcon={this.props.appIcon}
                  history={this.props.history}
                  sidebarActions={this.props.sidebarActions}
                  sidebarChildren={this.props.sidebarChildren}
                  onSidebarToggle={this.sidebarToggleHandler}
                />

                {/* GoBack Button */}
                {this.props.showBackButton && (
                  <Styled.BoxTopbarIcon onClick={() => this.goBack(this.props.history)}>
                    <Icons.ArrowBack />
                    <MUI.Box padding="8px">
                      <Typography.Text>{i18n.t('Topbar.goBack')}</Typography.Text>
                    </MUI.Box>
                  </Styled.BoxTopbarIcon>
                )}
              </>
            )}

            <Styled.TalentryLogo
              src="https://assets.talentry.io/logo/TAL_logo.svg"
              alt="Talentry"
            />

            {this.props.appIcon && (
              <>
                <Styled.Divider orientation="vertical" />
                <Styled.AppIcon
                  src={this.props.appIcon}
                  alt={this.props.appName}
                />
                {/* <Styled.AppName>
                  {this.props.appName}
                </Styled.AppName> */}
              </>
            )}

            <Styled.Span />

            {!isExternal && (
              <>
                {/* Menú Notificaciones */}
                <MenuNotifications />

                {/* Menú Apps */}
                <MenuApps
                  history={this.props.history}
                />

                {/* Menú Administrador */}
                <MenuAdmin
                  history={this.props.history}
                  appName={this.props.appName}
                  adminActions={this.props.adminActions}
                  customAdminActions={this.props.customAdminActions}
                />
              </>
            )}

            {/* Menú Usuario */}
            <MenuUser
              history={this.props.history}
              isExternal={isExternal}
            />
          </Styled.Toolbar>

          <Lines
            direction="horizontal"
            firstLine={{ size: 75, color: 'blueMedium' }}
            secondLine={{ size: 25, color: 'red' }}
          />
        </Styled.AppBar>

        {/* MODAL - Sesión por expirar */}
        <MUI.Dialog
          open={showSessionExpiring}
          onClose={this.onClose}
          maxWidth="sm"
          fullWidth
        >
          <MUI.DialogTitle>
            <Flex.Col>
              {i18n.t('Login.sessionExpiringTitle')}
              <Typography.Text color="greyDarkAlpha">
                {`${i18n.t('Login.sessionOf')}: `}
                <Typography.Span>{'Talentry'}</Typography.Span>
              </Typography.Text>
            </Flex.Col>
            <MUI.DialogBoxClose onClick={this.onClose}>
              <Icons.Close />
            </MUI.DialogBoxClose>
          </MUI.DialogTitle>
          <MUI.DialogContent style={{ position: 'relative', overflow: 'hidden' }}>
            <Flex.Col padding="16px">
              <Typography.Text>{i18n.t('Login.sessionExpiringBody')}</Typography.Text>
              {!!remainLabel && (
                <Flex.Col style={{ marginTop: '16px' }}>
                  <Typography.Medium>{`${i18n.t('Login.timeRemaining')}: `}</Typography.Medium>
                  <Typography.H4>{remainLabel}</Typography.H4>
                </Flex.Col>
              )}
            </Flex.Col>
            <Icons.Time style={{ position: 'absolute', right: '-35px', bottom: '-45px', fontSize: '150px', color: Theme.colors.redDarkAlpha }} />
          </MUI.DialogContent>
          <MUI.DialogActions>
            <MUI.ButtonText
              onClick={this.onLogoutNow}
              disabled={isRefreshing}
              style={{ color: Theme.colors.red }}
            >
              {i18n.t('Login.closeSession')}
            </MUI.ButtonText>
            <MUI.ButtonCTA
              type="button"
              color="primary"
              onClick={this.onContinueSession}
              disabled={isRefreshing}
              autoFocus
            >
              {isRefreshing ? i18n.t('Login.extendingSession') : i18n.t('Login.continueSession')}
            </MUI.ButtonCTA>
          </MUI.DialogActions>
        </MUI.Dialog>
      </>
    );
  }
}

Topbar.propTypes = {
  // History router.
  history: PropTypes.object,
  // Nombre de la aplicación de Talentry.
  appName: PropTypes.string,
  // URL del icono de la aplicación de Talentry.
  appIcon: PropTypes.string,
  // Array de acciones de administrador
  adminActions: PropTypes.array,
  // Array de acciones de sidebar.
  sidebarActions: PropTypes.array,
  // Componente de React dentro del Popover.
  customAdminActions: PropTypes.node,
  // Componente de React dentro del Drawer.
  sidebarChildren: PropTypes.node,
  // Boolean que indica si se muestra el botón de 'Regresar'.
  showBackButton: PropTypes.bool,
  backRoute: PropTypes.string,
  // Boolean que indica si se muestra el menú lateral.
  showSidebar: PropTypes.bool,
  // Minutos antes de la expiración para mostrar la alerta
  sessionWarnMinutes: PropTypes.number
};

Topbar.defaultProps = {
  sessionWarnMinutes: 5
};

// *************************************
// STYLED COMPONENTS
// *************************************

const Styled = {};

Styled.AppBar = styled(MuiAppBar)`
  && {
    padding-right: 0px !important;
    box-shadow: none;
  }
`;

Styled.Toolbar = styled(MuiToolbar)`
  && {
    padding-right: 0px;
    padding-left: 0px;
    min-height: 60px;
    max-height: 60px;
    color: ${(props) => props.theme.colors.greyDark};
    background: ${(props) => props.theme.colors.white};
  }
`;

Styled.TalentryLogo = styled.img`
  height: 60px;
  margin: 5px 0 0 0;
`;

Styled.Span = styled.span`
  flex: 1;
  box-sizing: border-box;
`;

Styled.BoxTopbarIcon = styled(MUI.ButtonText)`
  &.MuiButton-root{
    display: flex;
    min-width: 60px;
    width: auto;
    height: 60px;
    padding: 12px;
    margin: 0;

    &:hover {
      text-decoration: none;
      background-color: ${(props) => props.theme.colors.greyHover};
      border-radius: 0%;
    }
  }
`;

Styled.Divider = styled(MUI.Divider)`
  &&.MuiDivider-root.MuiDivider-vertical {
    height: 30px;
    align-self: center;
    border-right-width: 1px;
    border-right-style: solid;
    border-right-color: ${(props) => props.theme.colors.greyLight};
  }
`;

Styled.AppName = styled.div`
  display: flex;
  align-items: center;
  height: 60px;
  padding: 12px;
  font-family: 'Bryant Pro Medium';
  font-size: ${(props) => props.theme.typography.bodyText};
`;

Styled.AppIcon = styled.img`
  height: 30px;
  margin: 0px 0px 0px 16px;
`;

export default Topbar;
